import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { RotateCcw, Plus, Menu, X } from 'lucide-react';
import MessageBubble from './MessageBubble';
import ChatInput from './ChatInput';
import FloatingVoiceButton from './FloatingVoiceButton';
import LanguageSelector from './LanguageSelector';
import Logo from './Logo';
import Footer from './Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

class RealtimeAudioChat {
  constructor() {
    this.peerConnection = null;
    this.dataChannel = null;
    this.audioElement = null;
    this.onStatusChange = null;
  }

  async init() {
    try {
      const tokenResponse = await fetch(`${BACKEND_URL}/api/v1/realtime/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });
      
      if (!tokenResponse.ok) throw new Error(`Failed to get session token: ${tokenResponse.status}`);
      
      const data = await tokenResponse.json();
      if (!data.client_secret?.value) throw new Error("No client_secret in response");
      
      this.peerConnection = new RTCPeerConnection();
      this.setupAudioElement();
      await this.setupLocalAudio();
      this.setupDataChannel();
      
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);
      
      const response = await fetch(`${BACKEND_URL}/api/v1/realtime/negotiate`, {
        method: "POST",
        body: offer.sdp,
        headers: { "Content-Type": "application/sdp" }
      });
      
      if (!response.ok) throw new Error(`Negotiation failed: ${response.status}`);
      
      const { sdp: answerSdp } = await response.json();
      await this.peerConnection.setRemoteDescription({
        type: "answer",
        sdp: answerSdp
      });
      
      if (this.onStatusChange) this.onStatusChange('connected');
    } catch (error) {
      console.error('Voice connection error:', error);
      if (this.onStatusChange) this.onStatusChange('error');
      throw error;
    }
  }

  setupAudioElement() {
    this.audioElement = document.createElement("audio");
    this.audioElement.autoplay = true;
    document.body.appendChild(this.audioElement);
    this.peerConnection.ontrack = (event) => {
      this.audioElement.srcObject = event.streams[0];
    };
  }

  async setupLocalAudio() {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => {
      this.peerConnection.addTrack(track, stream);
    });
  }

  setupDataChannel() {
    this.dataChannel = this.peerConnection.createDataChannel("oai-events");
  }

  disconnect() {
    if (this.dataChannel) this.dataChannel.close();
    if (this.peerConnection) this.peerConnection.close();
    if (this.audioElement) this.audioElement.remove();
    if (this.onStatusChange) this.onStatusChange('disconnected');
  }
}

const LucyChat = () => {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [language, setLanguage] = useState('en');
  const [loading, setLoading] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState('disconnected');
  const [menuOpen, setMenuOpen] = useState(false);
  const chatRef = useRef(null);
  const messagesEndRef = useRef(null);
  const scrollAreaRef = useRef(null);

  useEffect(() => {
    createNewSession();
    return () => {
      if (chatRef.current) chatRef.current.disconnect();
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const createNewSession = async () => {
    try {
      const response = await axios.post(`${API}/sessions`, { language });
      setSessionId(response.data.session_id);
      setMessages([]);
      setMenuOpen(false);
      
      // Send welcome message
      setTimeout(() => {
        setMessages([{
          message_id: 'welcome',
          role: 'assistant',
          content: `Hello! I'm Lucy, your warm and empathetic AI companion. I'm here to listen, understand, and help you explore your thoughts and feelings. How are you today?`,
          language,
          timestamp: new Date().toISOString()
        }]);
      }, 500);
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };

  const clearHistory = async () => {
    if (!sessionId) return;
    try {
      await axios.delete(`${API}/sessions/${sessionId}`);
      createNewSession();
      setMenuOpen(false);
    } catch (error) {
      console.error('Error clearing history:', error);
    }
  };

  const sendMessage = async (content, attachments = []) => {
    if (!sessionId || (!content.trim() && attachments.length === 0)) return;

    const userMessage = {
      message_id: `temp-${Date.now()}`,
      role: 'user',
      content,
      language,
      timestamp: new Date().toISOString(),
      attachments: attachments.length > 0 ? attachments : null
    };

    setMessages(prev => [...prev, userMessage]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: content || (attachments.length > 0 ? `[${attachments.length} fichier(s) envoyé(s)]` : ''),
        language,
        attachments: attachments.length > 0 ? attachments : null
      });

      const assistantMessage = {
        message_id: response.data.message_id,
        role: 'assistant',
        content: response.data.response,
        language,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [
        ...prev.slice(0, -1),
        { ...userMessage, message_id: `user-${Date.now()}` },
        assistantMessage
      ]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const toggleVoice = async () => {
    if (voiceStatus === 'disconnected') {
      if (chatRef.current) chatRef.current.disconnect();
      
      setVoiceStatus('connecting');
      const chat = new RealtimeAudioChat();
      chat.onStatusChange = setVoiceStatus;
      chatRef.current = chat;

      try {
        await chat.init();
      } catch (error) {
        console.error('Voice connection failed:', error);
        setVoiceStatus('error');
      }
    } else {
      if (chatRef.current) {
        chatRef.current.disconnect();
        chatRef.current = null;
      }
      setVoiceStatus('disconnected');
    }
  };

  return (
    <div
      className="min-h-screen w-full overflow-hidden flex flex-col"
      style={{
        background: 'linear-gradient(140deg, #08101e 0%, #0f172a 55%, #0b3b4b 100%)'
      }}
    >
      {/* Header - Responsive */}
      <motion.header
        className="sticky top-0 z-40 backdrop-blur-xl border-b"
        style={{
          background: 'rgba(11, 18, 32, 0.7)',
          borderColor: 'rgba(var(--line), 0.6)'
        }}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo - Always visible */}
            <Logo />

            {/* Desktop Controls */}
            <div className="hidden md:flex items-center gap-3">
              <LanguageSelector value={language} onChange={setLanguage} />
              
              <button
                onClick={clearHistory}
                className="px-4 py-2 rounded-lg bg-sky-500/10 text-sky-300 border border-sky-400/30 hover:bg-sky-400/15 transition-all flex items-center gap-2"
                data-testid="clear-history-button"
              >
                <RotateCcw size={16} />
                <span>Clear</span>
              </button>
              
              <button
                onClick={createNewSession}
                className="px-4 py-2 rounded-lg text-zinc-950 transition-all flex items-center gap-2"
                style={{ background: 'var(--accent-cyan)' }}
                data-testid="new-session-button"
              >
                <Plus size={16} />
                <span>New</span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden p-2 rounded-lg bg-sky-500/10 text-sky-300 border border-sky-400/30 hover:bg-sky-400/15 transition-all"
              data-testid="mobile-menu-button"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="md:hidden overflow-hidden border-t"
                style={{ borderColor: 'rgba(var(--line), 0.6)' }}
              >
                <div className="py-3 space-y-2">
                  <div className="px-2">
                    <LanguageSelector value={language} onChange={(val) => { setLanguage(val); setMenuOpen(false); }} />
                  </div>
                  
                  <button
                    onClick={clearHistory}
                    className="w-full px-4 py-3 rounded-lg bg-sky-500/10 text-sky-300 border border-sky-400/30 hover:bg-sky-400/15 transition-all flex items-center gap-3 text-left"
                  >
                    <RotateCcw size={18} />
                    <span className="font-medium">Clear History</span>
                  </button>
                  
                  <button
                    onClick={createNewSession}
                    className="w-full px-4 py-3 rounded-lg text-zinc-950 transition-all flex items-center gap-3 text-left font-medium"
                    style={{ background: 'var(--accent-cyan)' }}
                  >
                    <Plus size={18} />
                    <span>New Session</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.header>

      {/* Main content - Full width chat with flex-1 to push footer down */}
      <div className="flex-1 max-w-5xl mx-auto w-full">
        <div
          ref={scrollAreaRef}
          className="flex flex-col gap-4 px-3 sm:px-4 pt-4 pb-32 overflow-y-auto"
          style={{ maxHeight: 'calc(100vh - 56px - 48px)' }}
        >
          {messages.map((message, index) => (
            <MessageBubble key={message.message_id} message={message} index={index} />
          ))}
          
          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex gap-3 items-start"
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center bg-cyan-500/20 text-cyan-300">
                <div className="w-2 h-2 rounded-full bg-cyan-400 animate-ping" />
              </div>
              <div className="flex gap-2 p-4 rounded-2xl rounded-bl-md bg-cyan-500/10 border border-cyan-400/20">
                <div className="w-2 h-2 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-cyan-400 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <ChatInput onSend={sendMessage} disabled={loading || !sessionId} />
      </div>

      {/* Footer */}
      <Footer />

      {/* Floating voice button with Lucy's photo */}
      <FloatingVoiceButton
        status={voiceStatus}
        onClick={toggleVoice}
        disabled={!sessionId}
      />
    </div>
  );
};

export default LucyChat;
